import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { useDataMutation, useDeleteEntity, useUser } from '@work4all/data';

import { VacationRequestEntity } from '@work4all/models/lib/Classes/VacationRequestEntity.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { VacationRequestStatus } from '@work4all/models/lib/Enums/VacationRequestStatus.enum';

import { canApproveVacations } from '@work4all/utils/lib/permissions';

import { useApendCustomListSettings } from '../../../containers/file-entities-lists/useApendCustomListSettings';
import { usePageTitle } from '../../../hooks';
import { settings, useSetting } from '../../../settings';
import { useBottomViews } from '../../bottom-views';
import { ApprovalStatuses, useApprovalActions } from '../approval-actions';
import { ApprovalContextProvider } from '../approval-actions/approval-context';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { GridStateProvider } from '../grid/GridStateProvider';
import schema from '../schemata/vacationApproval-table-schema.json';
import { useTableLayoutState } from '../table-layout';
import { useErpHandlersTemplateV8 } from '../use-erp-handlers-template-v8';
import { VacationEndDateCell } from '../VacationEndDateCell';

import { VacationStatusCardIconCell } from './cells/VacationStatusCardIconCell';
import { VacationStatusCell } from './cells/VacationStatusCell';
import { FullCalendarView } from './components/FullCalendarView';
import { mapSelectedRowStatus } from './utils';

const defaultSort = [
  { field: 'insertTime', direction: SortDirection.DESCENDING },
];

const forceRequestFields: VacationRequestEntity = {
  vacationItems: [
    {
      id: null,
      approveDate: null,
      date: null,
      vacationApprover: {
        id: null,
        displayName: null,
      },
    },
  ],
};

// const DISABLED_COLUMNS = ['unused'];

export function VacationApprovalTable(props: IEntityTableV8) {
  const { t } = useTranslation();

  const user = useUser();
  const canApprove = canApproveVacations(user);
  const layoutState = useTableLayoutState();
  const [layout] = layoutState;

  const entityType = Entities.vacationRequestEntity;

  const dataTable = useEntityDataTableV8<VacationRequestEntity, never>({
    ...props,
    schema: schema as never,
    defaultSort,
    cells: {
      VacationStatus: VacationStatusCell,
      VacationStatusCardIcon: VacationStatusCardIconCell,
      VacationEndDate: VacationEndDateCell,
    },
    rowModifiers(row) {
      const isApproved = row.status === VacationRequestStatus.DONE;
      const isRejected = row.status === VacationRequestStatus.REJECTED;
      const isShade1 = isApproved || isRejected;

      return { isShade1 };
    },
    forceRequestFields,
    enableMultiRowSelection: false,
  });

  const template = useErpHandlersTemplateV8(
    dataTable.table,
    'supplier.id',
    Entities.supplier
  );

  const bottomArea = useBottomViews({
    skip: !dataTable.selectedEntity,
    size: 'fill',
    views: [
      {
        type: 'custom',
        content: (
          <FullCalendarView
            beginDate={
              dataTable.selectedEntity?.dateFrom ??
              dataTable.selectedEntity?.vacationItems[0]?.date
            }
            department={dataTable.selectedEntity?.user?.departmentName}
          />
        ),
        titleTranslationKey: 'VACATION.TITLE',
      },
    ],
  });

  const [deleteEntity] = useDeleteEntity(false);

  const [mutate] = useDataMutation({
    entity: Entities.vacation,
    mutationType: EMode.upsert,
    responseData: { id: null },
    translateRelationInput: false,
  });

  const handleStatusChange = async (id: string, status: ApprovalStatuses) => {
    const vacationDayIds = (dataTable.data as VacationRequestEntity[])
      ?.find((request) => request.id === id)
      ?.vacationItems?.map((vacationItem) => vacationItem.id);
    const dateNow = DateTime.now().toISO();

    if (status === ApprovalStatuses.approve) {
      await Promise.all(
        vacationDayIds.map((vacationId) =>
          mutate({
            id: vacationId,
            approverUserId: user.benutzerCode,
            approveDate: dateNow,
          })
        )
      );
      await dataTable?.refetch();
    } else if (status === ApprovalStatuses.reject) {
      await deleteEntity({
        ids: [...vacationDayIds.map(String)],
        type: Entities.vacation,
      });
      await dataTable?.refetch();
    } else {
      return null;
    }
  };

  const { approvalActionDefinitions } = useApprovalActions({
    selectedId: dataTable.selectedEntity?.id,
    seletedRowStatus: mapSelectedRowStatus(dataTable.selectedEntity?.status),
    canApprove,
    canEdit: false,
    canForward: false,
    hideForward: true,
    editHandler: undefined,
    statusHandler: handleStatusChange,
  });

  usePageTitle(t('APPROVAL_CENTER.VACATION.PAGE_TITLE'));
  useApendCustomListSettings(entityType);

  const savedTableConfig = useSetting(
    settings.tableConfig({ entityType: entityType, layout })
  );

  return (
    <GridStateProvider>
      <ApprovalContextProvider value={{ onClick: handleStatusChange }}>
        <EntityTableV8
          {...props}
          {...dataTable}
          template={template}
          areas={{
            bottom: bottomArea,
          }}
          displayFooter
          onRowDoubleClick={() => true}
          overrides={{
            actions: {
              custom: {
                hideDivider: true,
                left: approvalActionDefinitions,
              },
              resetColumns: savedTableConfig.delete,
            },
          }}
          // disabledColumns={DISABLED_COLUMNS}
        />
      </ApprovalContextProvider>
    </GridStateProvider>
  );
}
